// components/Form/SearchBox.tsx
import { useState, useEffect, useRef } from 'react';
import { FormField } from '../FormField';
import './searchBox.css';
 
type SearchBoxProps<T> = {
  label?: string;
  data: T[];
  value: any;
  onSelect: (item: any | null) => void;
  searchKey: any;
  idKey: string;
  displayKey: string;
  placeholder?: string;
  error?: string;
  className?: string;
};
 
const SearchBox = <T extends object>({
  label,
  data,
  value,
  onSelect,
  searchKey,
  idKey,
  displayKey,
  placeholder = 'Buscar...',
  error,
  className
}: SearchBoxProps<T>) => {
 
  const getInputValueFromValue = (key: any) => {
	const item: any = data.find((d: any) => d[idKey] === key)
	return item ? item[displayKey] as string : '<no value>'
  }
 
  const [inputValue, setInputValue] = useState(value ? getInputValueFromValue(value) : '');
  const [filteredData, setFilteredData] = useState<T[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
 
  useEffect(() => {
	if (inputValue.length > 0) {
	  const lowerValue = inputValue.toLowerCase();
	  const results = data
		.filter((item: any) => 
		  String(item[searchKey]).toLowerCase().includes(lowerValue))
		.slice(0, 4);
	   
	  setFilteredData(results);
	  setShowResults(true);
	} else {
	  setFilteredData([]);
	  setShowResults(false);
	}
  }, [inputValue, data, searchKey]);
 
  useEffect(() => {
	const handleClickOutside = (e: MouseEvent) => {
	  if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
		setShowResults(false);
	  }
	};
 
	document.addEventListener('mousedown', handleClickOutside);
	return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
 
  const handleSelect = (item: any) => {
	setInputValue(String(item[displayKey]));
	setSelectedItem(item);
	onSelect(item);
	setShowResults(false);
  };
 
  const hasMoreResults = data.filter((item: any) =>
	String(item[searchKey]).toLowerCase().includes(inputValue.toLowerCase())
  ).length > 4;
 
  return (
	<FormField label={label} error={error} className={className}>
	  <div className="searchbox-container" ref={wrapperRef}>
		<input
		  type="text"
		  className="searchbox-input"
		  value={inputValue}
		  onChange={(e) => {
			setInputValue(e.target.value);
			if (e.target.value === '') {
			  onSelect(null);
			  setSelectedItem(null);
			}
		  }}
		  placeholder={placeholder}
		  onFocus={() => setShowResults(true)}
		/>
		 
		{showResults && inputValue.length > 0 && (
		  <div className="search-results">
			{filteredData.length > 0 ? (
			  <>
				{filteredData.map((item: any, index) => (
				  <div
					key={String(item[idKey])}
					className="result-item"
					onClick={() => handleSelect(item)}
				  >
					{String(item[displayKey])}
				  </div>
				))}
				{hasMoreResults && (
				  <div className="more-results">
					Hay más opciones disponibles...
				  </div>
				)}
			  </>
			) : (
			  <div className="no-results">No se encontraron resultados</div>
			)}
		  </div>
		)}
	  </div>
	</FormField>
  );
};
 
export default SearchBox;